import type { CourseDateAnnotationSingle } from "@shared/models/course"
import DateAnnotation from "@shared/models/date-annotation"
import type { NoteWords } from "@shared/unified-tiptap/extensions/date-annotation-note/date-annotation-note-extension"
import ObjectId from "@shared/util/object-id-gen"

/**
 * The annotation that turns a class off for a day, and nothing else. It carries no words and is
 * never painted: `isOff` is what `findCourseDatesOff` reads, and `showAnnotation: false` keeps it
 * out of the day's notes. Keeping the off state in its own annotation is what lets a note outlive
 * the class being turned back on — see [dayNoteAnnotation].
 */
export function dayOffAnnotation({
  dateString,
}: {
  dateString: string
}): CourseDateAnnotationSingle {
  const model: CourseDateAnnotationSingle = {
    id: ObjectId.create(),
    type: "date-annotation-course-custom-single",
    attributes: {
      title: null,
      description: null,
      isOff: true,
      isPublic: false,
      dateString,
      showAnnotation: false,
    },
  }
  // Structor defines its fields at runtime (defineStruct), so the class type can't say it
  // carries the model's shape. The literal above is what the interface checks.
  return new DateAnnotation(model) as unknown as CourseDateAnnotationSingle
}

/**
 * A note pinned to a day, which says nothing about whether the class meets — `isOff: false`, so
 * it never reaches `datesOff`. Turning the class on and off leaves it where it is.
 */
export function dayNoteAnnotation({
  dateString,
  note,
}: {
  dateString: string
  note: NoteWords
}): CourseDateAnnotationSingle {
  const model: CourseDateAnnotationSingle = {
    id: ObjectId.create(),
    type: "date-annotation-course-custom-single",
    attributes: {
      title: note.title,
      description: note.description,
      isOff: false,
      isPublic: false,
      dateString,
      showAnnotation: true,
    },
  }
  return new DateAnnotation(model) as unknown as CourseDateAnnotationSingle
}
